import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail, Clock, Leaf, Scale, Sun, Users } from "lucide-react";
import Reveal from "@/components/Reveal";
import { STORE, IMAGES } from "@/data/store";
import { CATEGORIES } from "@/data/products";

const HERO_LINES = ["Pure Sourcing.", "Heritage Kirana.", "Honest Quality."];

const MaskedLine = ({ text, index }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay: 0.25 + index * 0.14, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

const MANIFESTO = [
  { n: "01", icon: Leaf, title: "Direct Harvest", text: "We buy straight from farmers around Sanganer, Mathania and Nagaur — no middlemen, no cold-storage staleness. What leaves the farm in the morning reaches your thali by evening." },
  { n: "02", icon: Scale, title: "Honest Pricing", text: "The same fair price for every customer, every day. We weigh in front of you, print the rate on the board, and have done so since 1976." },
  { n: "03", icon: Sun, title: "Fresh, Daily", text: "Paneer set at dawn, milk delivered twice a day, greens from the morning mandi. If it isn't fresh today, it isn't on our shelf today." },
  { n: "04", icon: Users, title: "Community First", text: "Three generations of the same families shop with us. We keep khata ledgers, deliver to elders, and know your usual order by heart." },
];

const TESTIMONIALS = [
  { quote: "My mother shopped here, and now my children do. The garam masala is the same recipe it was forty years ago.", name: "Shanta Devi", role: "Customer for 38 years" },
  { quote: "Arjun ji weighs everything in front of you and never rounds against the customer. That honesty is rare today.", name: "Ramesh Choudhary", role: "Local resident" },
  { quote: "The Mathania chilli and A2 ghee are better than anything I find in the big city supermarkets. Worth the trip.", name: "Priya Sharma", role: "Home chef" },
];

const MARQUEE_ITEMS = ["DIRECT HARVEST", "100% UNADULTERATED", "HERITAGE KIRANA", "FRESH DAILY PRODUCE", "FREE LOCAL DELIVERY", "EST. 1976"];

export default function Home() {
  const { scrollY } = useScroll();
  const heroImgY = useTransform(scrollY, [0, 700], [0, 110]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-32 lg:pt-40 pb-16 lg:pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center gap-3 rounded-full border border-border px-4 py-2 mb-8"
              data-testid="hero-heritage-badge"
            >
              <span className="h-2 w-2 rounded-full bg-terra animate-pulse" />
              <span className="font-code text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                Est. {STORE.est} · Rajasthan · Family-Run
              </span>
            </motion.div>

            <h1
              data-testid="hero-headline"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08]"
            >
              {HERO_LINES.map((line, i) => (
                <MaskedLine key={line} text={line} index={i} />
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl mt-7"
            >
              A kirana store the way it was meant to be — farm-direct produce, stone-ground
              spices, honest scales and a familiar face behind the counter. Serving our
              community for nearly five decades.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="flex flex-wrap items-center gap-4 mt-10"
            >
              <Link
                to="/products"
                data-testid="hero-cta-explore-products"
                className="group inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground px-7 py-3.5 font-sub text-sm font-semibold tracking-wide hover:bg-terra transition-colors duration-300"
              >
                Explore Products
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact-us"
                className="inline-flex items-center gap-3 rounded-full border border-foreground/25 px-7 py-3.5 font-sub text-sm font-semibold tracking-wide hover:border-terra hover:text-terra transition-colors duration-300"
              >
                Contact Us
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.15 }}
              className="flex flex-wrap gap-x-10 gap-y-4 mt-12"
              data-testid="hero-stats"
            >
              {[["48+", "Years of Trust"], ["500+", "Products Stocked"], ["1000s", "of Families Served"]].map(([num, label]) => (
                <div key={label}>
                  <p className="font-display text-3xl font-bold text-terra">{num}</p>
                  <p className="font-code text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{label}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -inset-3 rounded-[2.5rem] border border-terra/40 rotate-2" aria-hidden="true" />
              <div className="relative rounded-[2.5rem] overflow-hidden aspect-[4/5]">
                <motion.img
                  src={IMAGES.hero}
                  alt="Fresh produce display at Arjun Ram Harijan kirana store"
                  data-testid="hero-spotlight-image"
                  style={{ y: heroImgY, scale: 1.15 }}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3">
                  <div>
                    <p className="font-code text-[10px] uppercase tracking-[0.25em] text-white/70">Today's Pick</p>
                    <p className="font-display text-2xl text-white font-semibold">Morning Mandi Fresh</p>
                  </div>
                  <p className="font-code text-xs text-white/80 text-right leading-relaxed">Picked 5 AM<br />Shelf 8 AM</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-border bg-primary text-primary-foreground overflow-hidden" data-testid="editorial-marquee">
        <div className="animate-marquee flex whitespace-nowrap py-4 will-change-transform">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center shrink-0" aria-hidden={dup === 1}>
              {MARQUEE_ITEMS.concat(MARQUEE_ITEMS).map((item, i) => (
                <span key={`${dup}-${i}`} className="flex items-center">
                  <span className="font-display text-lg italic px-6">{item}</span>
                  <span className="text-gold text-xs">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-20 lg:py-28" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <p className="font-code text-xs uppercase tracking-[0.25em] text-terra mb-4">Our Promise</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight leading-snug max-w-2xl">
              Four chapters of a promise we've kept since {STORE.est}.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mt-14">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.08}>
                <div
                  data-testid={`manifesto-chapter-${m.n}`}
                  className="group h-full rounded-3xl border border-border bg-card p-8 lg:p-10 hover:border-terra/50 hover:shadow-xl hover:shadow-terra/5 transition-all duration-300"
                >
                  <div className="flex items-start justify-between">
                    <span className="font-display text-5xl font-bold text-terra/25 group-hover:text-terra/60 transition-colors duration-300">{m.n}</span>
                    <m.icon size={22} className="text-terra mt-2" strokeWidth={1.8} />
                  </div>
                  <h3 className="font-display text-xl sm:text-2xl font-medium leading-snug mt-6">{m.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-3">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORY SHOWCASE */}
      <section className="py-20 lg:py-28 bg-secondary/50 border-y border-border" data-testid="category-showcase">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-code text-xs uppercase tracking-[0.25em] text-terra mb-4">The Shelves</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight leading-snug">
                Six aisles. Zero shortcuts.
              </h2>
            </div>
            <Link
              to="/products"
              data-testid="showcase-view-all-link"
              className="group inline-flex items-center gap-2 font-sub text-sm font-semibold link-underline"
            >
              View full catalogue
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-14">
            {CATEGORIES.map((cat, i) => (
              <Reveal key={cat.id} delay={i * 0.06}>
                <Link
                  to={`/products?cat=${cat.id}`}
                  data-testid={`category-card-${cat.id}`}
                  className="group block"
                >
                  <div className="relative rounded-3xl overflow-hidden aspect-[4/3]">
                    <img
                      src={cat.image}
                      alt={cat.label}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="font-code text-[10px] uppercase tracking-[0.25em] text-white/70 mb-1.5">
                        Aisle {String(i + 1).padStart(2, "0")}
                      </p>
                      <p className="font-display text-2xl text-white font-semibold">{cat.label}</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-4 px-1">{cat.blurb}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HERITAGE STORY */}
      <section className="py-20 lg:py-28" data-testid="heritage-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <p className="font-code text-xs uppercase tracking-[0.25em] text-terra mb-4">Our Story</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight leading-snug">
              It began with one wooden counter and a brass scale in {STORE.est}.
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mt-6">
              Arjun Ram Harijan opened this shop with a simple rule: sell only what you would
              feed your own family. Nearly five decades later, the counter is newer and the
              shelves are fuller — but the rule hasn't moved an inch.
            </p>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mt-4">
              Today we still grind our masalas in small batches, still buy from the same
              farming families, and still remember how your grandmother took her tea.
            </p>
            <Link
              to="/about"
              data-testid="heritage-read-more-link"
              className="group inline-flex items-center gap-2 font-sub text-sm font-semibold mt-8 link-underline"
            >
              Read our full story
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal className="lg:col-span-6" delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2.5rem] bg-sage -rotate-1" aria-hidden="true" />
              <img
                src={IMAGES.aboutStore}
                alt="Inside the Arjun Ram Harijan store"
                data-testid="heritage-story-image"
                loading="lazy"
                className="relative rounded-[2.5rem] w-full aspect-[4/3] object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-28 bg-secondary/50 border-y border-border" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <p className="font-code text-xs uppercase tracking-[0.25em] text-terra mb-4">Word of Mouth</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight leading-snug max-w-xl">
              Trusted by the neighbourhood, generation after generation.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-14">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08}>
                <figure
                  data-testid={`testimonial-card-${i}`}
                  className="h-full rounded-3xl border border-border bg-card p-8 flex flex-col hover:border-terra/50 transition-colors duration-300"
                >
                  <span className="font-display text-6xl leading-none text-terra/30">"</span>
                  <blockquote className="text-base leading-relaxed mt-2 flex-1">{t.quote}</blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-border">
                    <p className="font-sub font-semibold text-sm">{t.name}</p>
                    <p className="font-code text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{t.role}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUICK CONTACT BAR */}
      <section className="py-20 lg:py-24" data-testid="quick-contact-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <div className="rounded-[2.5rem] bg-primary text-primary-foreground p-10 lg:p-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative overflow-hidden">
              <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full border border-primary-foreground/10" aria-hidden="true" />
              <div className="absolute -right-8 -bottom-20 h-56 w-56 rounded-full border border-primary-foreground/10" aria-hidden="true" />
              <div className="lg:col-span-7 relative">
                <p className="font-code text-xs uppercase tracking-[0.25em] text-gold mb-4">Visit or Ring Us</p>
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight leading-snug">
                  Need something on the shelf before dinner? Just call.
                </h2>
                <p className="text-sm text-primary-foreground/70 leading-relaxed mt-4 max-w-lg">
                  We keep aside your order for pickup, and deliver free within the locality.
                  Store hours: {STORE.hours.map((h) => `${h.days} ${h.time}`).join(" · ")}.
                </p>
              </div>
              <div className="lg:col-span-5 relative flex flex-col gap-4">
                <a
                  href={`tel:+${STORE.phoneRaw}`}
                  data-testid="quick-call-button"
                  className="group inline-flex items-center justify-between gap-4 rounded-full bg-terra bg-terra-hover text-white px-7 py-4 font-sub text-sm font-semibold transition-colors duration-300"
                >
                  <span className="flex items-center gap-3"><Phone size={16} /> Call {STORE.phoneDisplay}</span>
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <a
                  href={`mailto:${STORE.email}`}
                  data-testid="quick-email-button"
                  className="group inline-flex items-center justify-between gap-4 rounded-full border border-primary-foreground/30 px-7 py-4 font-sub text-sm font-semibold hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  <span className="flex items-center gap-3"><Mail size={16} /> {STORE.email}</span>
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <p className="flex items-center gap-2 font-code text-[11px] uppercase tracking-[0.2em] text-primary-foreground/60 px-2">
                  <Clock size={12} /> Open today · Mon–Sat 8 AM – 9 PM
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
